import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ChartFacade,
  ChartId,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  confirmationDialogData,
  ConfirmDialogComponent,
  DialogPresets,
  type IConfirmationDialogInput,
} from '@principle-theorem/ng-shared';
import {
  ChartedTreatment,
  FeeScheduleManager,
} from '@principle-theorem/principle-core';
import {
  type AnyChartedItemConfiguration,
  type IChartedItem,
  type IChartedMultiStepTreatment,
  type IChartedMultiStepTreatmentStep,
  type IChartedTreatment,
  type IChartedTreatmentGroup,
  type IFeeSchedule,
  type ITreatmentPlan,
  type ITreatmentStep,
  isChartedTreatmentGroup,
} from '@principle-theorem/principle-core/interfaces';
import { snapshot, type WithRef } from '@principle-theorem/shared';
import { ChartDialogService } from '../chart-dialog.service';
import { type IEditChartableData } from '../chartable-surface-updater';
import {
  ChangeBasePriceDialogComponent,
  type IChangeBasePriceDialogData,
} from '../components/change-base-price-dialog/change-base-price-dialog.component';

@Injectable()
export class ChartedItemActionsService {
  private _dialog = inject(MatDialog);
  private _chartDialogService = inject(ChartDialogService);
  private _chartStore = inject(ChartFacade);
  private _snackBar = inject(MatSnackBar);

  async editSurfaces(
    config: AnyChartedItemConfiguration,
    item: IChartedItem,
    plan?: WithRef<ITreatmentPlan> | IChartedMultiStepTreatment,
    step?: WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep
  ): Promise<IEditChartableData | undefined> {
    const selectedSurfaces = await this._chartDialogService.getSurfaces(
      config,
      undefined,
      item
    );

    if (!selectedSurfaces) {
      return;
    }

    if (selectedSurfaces[0].unscoped) {
      this._snackBar.open('No surfaces available for this treatment');
      return;
    }

    const chartingAs = await snapshot(
      this._chartStore.chartingAs$(ChartId.InAppointment)
    );

    return {
      chartable: config,
      selectedSurfaces,
      item,
      chartingAs,
      plan,
      step,
    };
  }

  async addBasePrice(currentBasePrice: number): Promise<number | undefined> {
    const data: IChangeBasePriceDialogData = { basePrice: currentBasePrice };

    const response = await this._dialog
      .open<
        ChangeBasePriceDialogComponent,
        IChangeBasePriceDialogData,
        IChangeBasePriceDialogData
      >(ChangeBasePriceDialogComponent, DialogPresets.small({ data }))
      .afterClosed()
      .toPromise();

    return response?.basePrice;
  }

  async applyFeeSchedule(
    chartedTreatment: IChartedTreatment | IChartedTreatment[],
    feeSchedule: WithRef<IFeeSchedule>,
    stepTreatments: IChartedTreatment[] = []
  ): Promise<IChartedTreatment | IChartedTreatment[]> {
    const manager = await FeeScheduleManager.create(feeSchedule.ref);
    const treatments = Array.isArray(chartedTreatment)
      ? chartedTreatment
      : [chartedTreatment];
    const scopedConfigs =
      await ChartedTreatment.loadScopedTreatmentConfigs(treatments);

    if (Array.isArray(chartedTreatment)) {
      return chartedTreatment.map((treatment) =>
        ChartedTreatment.changeFeeSchedule(
          treatment,
          stepTreatments,
          manager,
          scopedConfigs
        )
      );
    }

    return ChartedTreatment.changeFeeSchedule(
      chartedTreatment,
      stepTreatments,
      manager,
      scopedConfigs
    );
  }

  async confirmDelete(
    treatment: IChartedTreatment | IChartedTreatmentGroup
  ): Promise<boolean> {
    const isGroup = isChartedTreatmentGroup(treatment);

    const data = confirmationDialogData({
      title: isGroup ? 'Delete Treatment Group' : 'Delete Treatment',
      prompt: isGroup
        ? `Are you sure you want to delete this treatment group?`
        : `Are you sure you want to delete this treatment?`,
      submitLabel: 'Delete',
      submitColor: 'warn',
    });

    const confirmed = await this._dialog
      .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
        ConfirmDialogComponent,
        DialogPresets.small({ data })
      )
      .afterClosed()
      .toPromise();

    return confirmed ?? false;
  }
}
